import React from 'react';
import { Navigate, Route } from 'react-router-dom';

import Enums from '../constants/enums';
import { isUserAuthenticated, getLoggedInUser } from '../helpers/authUtils';

import Login from '../pages/auth/login/login.route';
import LoginVerify from '../pages/auth/login-verify/login-verify.route';

// lazy load all the views

// auth
const LoginToken = React.lazy(() => import('../pages/auth/LoginToken'));
const Logout = React.lazy(() => import('../pages/auth/Logout'));
const ForgetPassword = React.lazy(() => import('../pages/auth/ForgetPassword'));
const ResetPassword = React.lazy(() => import('../pages/auth/ResetPassword'));

// settings
const Bookmarkers = React.lazy(() => import('../pages/settings/Bookmarkers'));
const BoostSpeed = React.lazy(() => import('../pages/settings/BoostSpeed'));
const CrbWebhook = React.lazy(() => import('../pages/settings/CrbWebhook'));
const AffiliatesListPage = React.lazy(() =>
    import('../pages/settings/affiliates/affiliates-list.container.view'),
);
const UserTierPage = React.lazy(() => import('../pages/settings/UserTier'));
const CashbackManagement = React.lazy(() => import('../pages/settings/CashbackManagement'));
const AppCompatibility = React.lazy(() => import('../pages/settings/app-compatibility/app-compatibility.controller'));
const DepositMinimumPage = React.lazy(() => import('../pages/settings/deposit-minimum/deposit-minimum.page'));
const ConnectVendorsList = React.lazy(() =>
    import('../pages/settings/connect-vendors/connect-vendors-list/connect-vendors-list.page'),
);
const VipManagementPage = React.lazy(() => import('../pages/user-administration/vip-management/vip-management.page'));

// administravia
const AdminUsers = React.lazy(() => import('../pages/administravia/admin-users/admin-users.page'));
const GalileoSimulator = React.lazy(() => import('../pages/administravia/GalileoSimulator/GalileoSimulator'));
const ManagePermissions = React.lazy(() => import('../pages/administravia/ManagePermissions/ManagePermissions'));
const VipHostsPage = React.lazy(() => import('../pages/settings/vip-hosts/vip-hosts.page'));

// inspect
const AuthorizationsListPage = React.lazy(
    () => import('../pages/inspect/authorizations-list/authorizations-list.page')
);
const AchQueuePage = React.lazy(() => import('../pages/inspect/ach-queue/ach-queue.page'));
const AchRequestPage = React.lazy(() => import('../pages/inspect/ach-request/ach-request.page'));
const ListBankAccountsPage = React.lazy(
    () => import('../pages/inspect/bank-accounts-list/bank-accounts-list.page')
);
const AchReportPage = React.lazy(() => import('../pages/inspect/AchReport'));
const AchReturnInboxPage = React.lazy(
    () => import('../pages/inspect/ach-return-inbox/ach-return-inbox.page')
);

// Transfer Desk (incoming wire + external ACH credits)
const TransferDeskPage = React.lazy(
    () => import('../pages/inspect/transfer-desk/transfer-desk.page')
);
// Bank accounts of venues / clearing firms that belong to no member (EDGE-3010)
const VenueSenderAccountsPage = React.lazy(
    () => import('../pages/inspect/venue-sender-accounts/venue-sender-accounts.page')
);
const OutboundWiresPage = React.lazy(() => import('../pages/inspect/outbound-wires/outbound-wires.page'));

// resource
const AchLimitsPage = React.lazy(() => import('../pages/resource/AchLimit'));

// dashboard
const FinancialMetricsPage = React.lazy(
    () => import('../pages/dashboard/financial-metrics/financial-metrics.page')
);
const VSSReportsPage = React.lazy(() => import('../pages/dashboard/VSSReports'));
const AffiliateUsersReportsPage = React.lazy(
    () => import('../pages/dashboard/affiliate-user-report/affiliate-user-report.page')
);
const EdgeCoreDashboard = React.lazy(() => import('../pages/dashboard/coreDashboard'));
const PlayerSuccessDashboard = React.lazy(() => import('../pages/dashboard/playerSuccessDashboard'));

// users
const UsersPage = React.lazy(() => import('../pages/user-administration/users/user-list/user-list.page'));

// pro businesses
const ProBusinessesPage = React.lazy(() => import('../pages/pro-businesses/businesses/pro.businesses.page'));

// webhooks
const WebhooksPage = React.lazy(() => import('../pages/webhooks/webhooks-list/webhooks-list.page'));

// risk
const SharedBanksPage = React.lazy(() => import('../pages/risk/shared-banks/shared-banks.page'));

// handle auth and authorization

const PrivateRoute = ({ component: Component, roles, ...rest }) => {
    if (!isUserAuthenticated()) {
        window.location.href = '/account/login';
        return null;
    }

    const loggedInUser = getLoggedInUser();
    // check if route is restricted by role
    if (roles && roles.indexOf(loggedInUser.role) === -1) {
        // role not authorised so redirect to home page
        return <Navigate to="/" replace />;
    }

    // authorised so return component
    return <Component {...rest} />;
};

// root routes
const rootRoute = {
    path: '/',
    component: () => <Navigate to="/dashboard" replace />,
    route: 'Route',
};

const dashboardRoutes = {
    name: 'Dashboards',
    route: PrivateRoute,
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
    icon: 'fa-solid fa-chart-mixed-up-circle-dollar',
    children: [
        {
            path: '/dashboard/',
            name: 'Financials',
            component: FinancialMetricsPage,
        },
        {
            path: '/VSS',
            name: 'VSS',
            component: VSSReportsPage,
        },
        {
            path: '/affiliateusers',
            name: 'Affiliate Users',
            component: AffiliateUsersReportsPage,
        },
        {
            path: '/edge-core-dash',
            name: 'Edge Core Dashboard',
            component: EdgeCoreDashboard,
        },
        {
            path: '/player-success',
            name: 'Player Success Dashboard',
            component: PlayerSuccessDashboard,
        },
    ],
};

const fraudRoutes = {
    name: 'Risk Management',
    route: PrivateRoute,
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
    icon: 'fa-sharp fa-face-disguise',
    children: [
        {
            path: '/fraud/shared-banks',
            name: 'Shared Banks',
            component: SharedBanksPage,
        },
    ],
};

const appsettingsRoutes = {
    path: '/settings',
    name: 'Application settings',
    route: PrivateRoute,
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
    icon: 'fa-solid fa-sliders',
    children: [
        {
            path: '/settings/bookmakers',
            name: 'Bookmakers',
            component: Bookmarkers,
            route: 'Route',
        },
        {
            path: '/settings/boost_speeds',
            name: 'Boost Speeds',
            component: BoostSpeed,
            route: 'Route',
        },
        {
            path: '/settings/crb-webhook',
            name: 'CRB Webhooks',
            component: CrbWebhook,
            route: 'Route',
        },
        {
            path: '/settings/list/affiliates',
            name: 'Affiliate Manager',
            component: AffiliatesListPage,
            route: 'Route',
        },
        {
            path: '/settings/list/ach-limits',
            name: 'Transaction Limits',
            component: AchLimitsPage,
            route: 'Route',
        },
        {
            path: '/settings/list/user-tiers',
            name: 'User Tiers',
            component: UserTierPage,
            route: 'Route',
        },
        {
            path: '/settings/cashback-management',
            name: 'Cashback Management',
            component: CashbackManagement,
            route: 'Route',
        },
        {
            path: '/settings/app-compatibility',
            name: 'App Compatibility',
            component: AppCompatibility,
            route: 'Route',
        },
        {
            path: '/settings/connect-venues',
            name: 'Connect Venues',
            component: ConnectVendorsList,
            route: 'Route',
        },
        {
            // Admin-only: the API's deposit-minimum endpoints are behind AdminGuard,
            // so support users would only reach a screen that 403s.
            path: '/settings/deposit-minimum',
            name: 'ACH Deposit Minimum',
            component: DepositMinimumPage,
            route: 'Route',
            roles: [Enums.UserRole.ADMIN],
        },
        {
            path: '/settings/vip-hosts',
            name: 'VIP Hosts',
            component: VipHostsPage,
            route: 'Route',
        },
    ],
};

const inspectorRoutes = {
    path: '/inspect',
    name: 'Inspectors',
    route: PrivateRoute,
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
    icon: 'fa-solid fa-face-thinking',
    children: [
        {
            path: '/inspect/bankaccounts',
            name: 'Bank Accounts',
            component: ListBankAccountsPage,
            route: 'Route',
        },
        {
            path: '/inspect/authorizations',
            name: 'Authorizations',
            component: AuthorizationsListPage,
            route: 'Route',
        },
        {
            path: '/inspect/ach-queue',
            name: 'ACH Queue',
            component: AchQueuePage,
            route: 'Route',
        },
        {
            path: '/inspect/ach-request',
            name: 'ACH Request',
            component: AchRequestPage,
            route: 'Route',
        },
        {
            path: '/inspect/ach-report',
            name: 'Monthly ACH Reports',
            component: AchReportPage,
            route: 'Route',
        },
        {
            path: '/inspect/ach-return-inbox',
            name: 'ACH Return Inbox',
            component: AchReturnInboxPage,
            route: 'Route',
        },
        {
            path: '/inspect/transfer-desk',
            name: 'Transfer Desk',
            component: TransferDeskPage,
            route: 'Route',
        },
        {
            path: '/inspect/outbound-wires',
            name: 'Outbound Wires',
            component: OutboundWiresPage,
            route: 'Route',
        },
        {
            path: '/inspect/venue-sender-accounts',
            name: 'Venue Sender Accounts',
            component: VenueSenderAccountsPage,
            route: 'Route',
        },
    ],
};

const administraviaRoutes = {
    path: '/administravia',
    name: 'Administravia',
    route: PrivateRoute,
    roles: [Enums.UserRole.ADMIN],
    icon: 'fa-solid fa-skull-crossbones',
    children: [
        {
            path: '/settings/admin_users',
            name: 'Admin Users',
            component: AdminUsers,
            route: 'Route',
        },
        {
            path: '/administravia/permissions',
            name: 'Manage Permissions',
            component: ManagePermissions,
            route: 'Route',
        },
        {
            path: '/simulator/galileo',
            name: 'Galileo Simulator',
            component: GalileoSimulator,
            route: 'Route',
        },
    ],
};



const webhookRoutes = {
    path: '/webhooks',
    name: 'Webhooks',
    route: PrivateRoute,
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
    icon: 'fa-solid fa-webhook',
    children: [
        {
            path: '/webhooks/galileo',
            name: 'From Galileo',
            component: WebhooksPage,
            route: 'Route',
            props: { source: 'Galileo' },
        },
        {
            path: '/webhooks/alloy',
            name: 'From Alloy',
            component: WebhooksPage,
            route: 'Route',
            props: { source: 'Alloy' },
        },
        {
            path: '/webhooks/crb',
            name: 'From CRB',
            component: WebhooksPage,
            route: 'Route',
            props: { source: 'CrossRiver' },
        },
        {
            path: '/webhooks/all',
            name: 'All Webhooks',
            component: WebhooksPage,
            route: 'Route',
        },
    ],
};

const userRoute = {
    path: '/users',
    name: 'User Administration',
    route: PrivateRoute,
    icon: 'fa-solid fa-users',
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
    children: [
        {
            path: '/users',
            name: 'User Management',
            component: UsersPage,
            route: 'Route',
        },
        {
            path: '/vip-management',
            name: 'VIP Management',
            component: VipManagementPage,
            route: 'Route',
        },
    ],
};

const proBusinessesRoute = {
    path: '/pro-businesses',
    name: 'Pro Businesses',
    component: ProBusinessesPage,
    route: 'Route',
    icon: 'fa-solid fa-building',
    roles: [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN],
};

const appRoutes = [
    dashboardRoutes,
    userRoute,
    proBusinessesRoute,
    fraudRoutes,
    appsettingsRoutes,
    webhookRoutes,
    inspectorRoutes,
    administraviaRoutes,
];

// auth
const authRoutes = {
    path: '/account',
    name: 'Auth',
    children: [
        {
            path: '/account/login',
            name: 'Login',
            component: Login,
        },
        {
            path: '/account/login-token',
            name: 'LoginToken',
            component: LoginToken,
        },
        {
            path: '/account/login-verify',
            name: 'LoginVerify',
            component: LoginVerify,
        },
        {
            path: '/account/logout',
            name: 'Logout',
            component: Logout,
        },
        {
            path: '/account/forget-password',
            name: 'ForgetPassword',
            component: ForgetPassword,
            route: Route,
        },
        {
            path: '/account/reset-password/:token',
            name: 'ResetPassword',
            component: ResetPassword,
            route: Route,
        },
    ],
};

// flatten the list of all nested routes, inheriting parent roles onto children
const flattenRoutes = (routes, parentRoles = []) => {
    let flatRoutes = [];

    routes = routes || [];
    routes.forEach((item) => {
        const roles = item.roles || parentRoles;
        const itemWithRoles = { ...item, roles };
        flatRoutes.push(itemWithRoles);

        if (typeof item.children !== 'undefined') {
            // Sort children alphabetically by name
            item.children.sort((a, b) => a.name.localeCompare(b.name));
            flatRoutes = [...flatRoutes, ...flattenRoutes(item.children, roles)];
        }
    });
    return flatRoutes;
};

// All routes
const allRoutes = [authRoutes, rootRoute, ...appRoutes];

const authProtectedRoutes = [...appRoutes];

const allFlattenRoutes = flattenRoutes(allRoutes);

export { allRoutes, authProtectedRoutes, allFlattenRoutes };
